import { exciseTaxTypeToRate, invoiceStrings } from '@data/invoice';
import { AdminInquiryDetail, AdminInquiryDetailHelper } from '@models/BookingInquiry';
import type {
  Invoice,
  InvoiceChargePayDayForm,
  InvoiceChargePayDayResponse,
  InvoiceDefaultValue,
  InvoiceDetails,
  InvoiceDetailsForm,
  InvoiceInfoForm,
  NumericInvoiceDetails,
} from '@models/next/Invoice';
import type { Currency } from '@models/next/Money';
import type { PreAlertDetails } from '@models/next/PreAlert';
import { TrackingData } from '@models/TrackingData';
import { type BaseUser } from '@models/User';
import { empty } from '@monoid-dev/mobx-zod-form';
import {
  type MessagesEither,
  firstLeftEither,
  makeMessagesEitherLeftIf,
  makeMessagesEitherLeft,
  makeMessagesEitherRight,
  makeMessagesEitherRightIf,
} from '@utils/errors';
import { roundPrice } from '@utils/types';
import { pipe } from 'remeda';

export class InvoiceHelper {
  static mapInvoiceDetailsToForm(v: InvoiceDetails): InvoiceDetailsForm {
    return {
      ...v,
      unit_num: v.unit_num ?? empty,
      unit_price: v.unit_price ?? empty,
      excise_tax_type: v.excise_tax_type || empty,
    };
  }

  static mapDefaultValueToChargePayDayForm(
    invoiceDefaultValue: InvoiceDefaultValue,
  ): InvoiceChargePayDayForm {
    return {
      ...invoiceDefaultValue!,
      client_id: 0,
      client_code: '',
      client_name: empty,
      payer_mark: 'jp',
      client_name_en: empty,
      client_zipcode: empty,
      client_address1: empty,
      client_address2: empty,
    };
  }

  static mapDefaultValueAndChargePayDayToInfoForm(
    invoiceDefaultValue: InvoiceDefaultValue,
    chargePayDayResponse: InvoiceChargePayDayResponse,
  ): InvoiceInfoForm {
    return {
      ...invoiceDefaultValue,
      ...chargePayDayResponse,
      conversion_rate: invoiceDefaultValue.conversion_rate ?? empty,
      conversion_rate_date: invoiceDefaultValue.conversion_rate_date ?? empty,
      language: '日本語',
    };
  }

  static mapInvoiceToChargePayDayForm(invoice: Invoice): InvoiceChargePayDayForm {
    return invoice;
  }

  static mapInvoiceToInfoForm(invoice: Invoice): InvoiceInfoForm {
    return invoice;
  }

  static canEditInvoiceDetail(
    bookingInquiry: AdminInquiryDetail,
    preAlert: PreAlertDetails,
    user: BaseUser,
    invoiceDetails: InvoiceDetails,
  ): MessagesEither {
    const adminInquiryDetailHelper = new AdminInquiryDetailHelper(bookingInquiry);

    // Only salesperson can edit.
    return firstLeftEither(
      () =>
        makeMessagesEitherLeftIf(!!invoiceDetails.invoice, {
          messages: [invoiceStrings.errorInvoiceDetailsUsed],
        }),
      () =>
        makeMessagesEitherLeftIf(invoiceDetails.country === 'none', {
          messages: [invoiceStrings.errorSalespersonEditCountryNone],
        }),
      () => adminInquiryDetailHelper.canEdit(user, preAlert),
      () =>
        makeMessagesEitherLeftIf(
          invoiceDetails.country === 'jp' && bookingInquiry.salesperson_japan?.email !== user.email,
          {
            messages: [invoiceStrings.errorNonSalespersonJPEditJP],
          },
        ),
    );
  }

  static canFinishInvoices(
    user: BaseUser,
    bookingInquiry: AdminInquiryDetail,
    trackingData: TrackingData,
  ): MessagesEither {
    return firstLeftEither(
      () => new AdminInquiryDetailHelper(bookingInquiry).canEditAsSalespersonJapan(user),
      () =>
        trackingData.data.お届け完了
          ? makeMessagesEitherRight()
          : makeMessagesEitherLeft({ messages: [invoiceStrings.errorNeedsDeliveredStatus] }),
      () =>
        trackingData.data.請求書全部発行完了
          ? makeMessagesEitherLeft({
              messages: [invoiceStrings.errorAlreadyInvoiceDone],
            })
          : makeMessagesEitherRight(),
    );
  }

  static canSelectInvoiceDetails(invoiceDetails: InvoiceDetails[]): MessagesEither {
    return firstLeftEither(() => {
      const notLocked = invoiceDetails.filter(
        (invoice) => invoice.country === 'cn' && invoice.is_locked === false,
      );

      return makeMessagesEitherLeftIf(notLocked.length > 0, {
        messages: [
          invoiceStrings.errorNeedLockCNInvoiceDetails(invoiceDetails.map((d) => d.invoice_name)),
        ],
      });
    });
  }

  static canCreateInvoice(user: BaseUser, preAlertDetails: PreAlertDetails): MessagesEither {
    return firstLeftEither(
      () =>
        makeMessagesEitherLeftIf(preAlertDetails.is_ledger_locked, {
          messages: [invoiceStrings.errorPreAlertLedgerLocked],
        }),
      () =>
        makeMessagesEitherRightIf(user.role === 'FCJapan' || user.role === 'admin', {
          messages: [invoiceStrings.errorNotFCJapan],
        }),
    );
  }

  static canUpdateInvoice(
    user: BaseUser,
    preAlertDetails: PreAlertDetails,
    invoice: Invoice,
  ): MessagesEither {
    return firstLeftEither(
      () => this.canCreateInvoice(user, preAlertDetails),
      () =>
        makeMessagesEitherRightIf(
          (user.role === 'FCJapan' && invoice.creator.email === user.email) ||
            user.role === 'admin',
          {
            messages: [invoiceStrings.errorCannotEditInvoice],
          },
        ),
    );
  }

  static getInvoiceDetailsComputed(item: NumericInvoiceDetails) {
    const unitPrice = item.unit_price && roundPrice(item.unit_price, item.currency);

    const currency = item.currency;

    const exciseTax = exciseTaxForCurrency(0, item.currency);

    const taxExcludedPrice = taxExcludedPriceForCurrency(0, item.currency);

    const totalPrice = totalPriceForCurrency(0, item.currency);

    function getPriceInCurrency(
      price: number | null | undefined,
      conversionRate: number,
      targetCurrency: Currency,
    ) {
      if (price == null) return price;

      if (currency === targetCurrency) {
        // JPY -> JPY
        // USD -> USD
        return roundPrice(price, targetCurrency);
      } else if (currency === 'USD' && targetCurrency === 'JPY') {
        // USD -> JPY
        return roundPrice(price * conversionRate, targetCurrency);
      } else {
        // JPY -> USD
        return roundPrice(price / conversionRate, targetCurrency);
      }
    }

    function unitPriceForCurrency(conversionRate: number, targetCurrency: Currency) {
      return getPriceInCurrency(unitPrice, conversionRate, targetCurrency);
    }

    function exciseTaxForCurrency(conversionRate: number, targetCurrency: Currency) {
      return pipe(
        taxExcludedPriceForCurrency(conversionRate, targetCurrency),
        (taxExcludedPrice) =>
          taxExcludedPrice &&
          roundPrice(taxExcludedPrice * exciseTaxTypeToRate[item.excise_tax_type], targetCurrency),
      );
    }

    function taxExcludedPriceForCurrency(conversionRate: number, targetCurrency: Currency) {
      return pipe(
        unitPriceForCurrency(conversionRate, targetCurrency),
        (unitPrice) =>
          unitPrice && item.unit_num && roundPrice(unitPrice * item.unit_num, targetCurrency),
      );
    }

    function totalPriceForCurrency(conversionRate: number, targetCurrency: Currency) {
      return pipe(
        [
          taxExcludedPriceForCurrency(conversionRate, targetCurrency),
          exciseTaxForCurrency(conversionRate, targetCurrency),
        ] as const,
        ([taxExcludedPrice, exciseTax]) =>
          taxExcludedPrice != null && exciseTax != null
            ? roundPrice(taxExcludedPrice + exciseTax, targetCurrency)
            : null,
      );
    }

    return {
      unitPrice,
      exciseTax,
      taxExcludedPrice,
      totalPrice,
      currency: item.currency,
      getPriceInCurrency,
      unitPriceForCurrency,
      exciseTaxForCurrency,
      taxExcludedPriceForCurrency,
      totalPriceForCurrency,
    } as const;
  }

  static isRevised(invoice: Invoice) {
    // Find whether there is a 発行済 and another status after it.
    // If so, the invoice must be locked then unlocked.
    return pipe(
      invoice.status.findIndex((s) => s.name === '発行済'),
      (i) => i !== -1 && i < invoice.status.length - 1,
    );
  }
}
